/**
 * The shared lock for staff "Preview as a parent" (#5634).
 *
 * One read allowlist, used three ways: the client fetch guard blocks any
 * `/api` call off it while a preview is on, middleware refuses any request
 * carrying the preview header that is off it, and the write-route inventory
 * test classifies every enroll-page call site against it.
 *
 * Pure module: safe to import from the browser, middleware and route code.
 */

/**
 * Where the preview runs. Each surface opens for one program type only, so a
 * lesson id never turns preview on in the class enroll page, or the reverse.
 * Step 1 of #5634 covers classes (open gym is a class); step 2 private lessons;
 * step 3 tryouts (a `tryout_events` row, not a program).
 */
export const PREVIEW_SURFACES = ['class', 'private_lesson', 'tryout'] as const;
export type PreviewSurface = (typeof PREVIEW_SURFACES)[number];

export const ENROLLMENT_PREVIEW_HEADER = 'x-cheervue-enrollment-preview';

/** The one refusal body every preview write lock returns (403). */
export const PREVIEW_WRITE_REFUSED_CODE = 'preview_write_refused';
export const PREVIEW_WRITE_REFUSED_BODY = {
  success: false,
  error: 'This is a preview. Nothing can be booked or saved.',
  code: PREVIEW_WRITE_REFUSED_CODE,
} as const;

/** Error bodies for the preview routes' staff gate (401 / 403 / 503). */
export function previewGateErrorBody(status: 401 | 403 | 503): { success: false; error: string } {
  if (status === 401) return { success: false, error: 'Not signed in.' };
  if (status === 403) return { success: false, error: 'Preview is for staff of this gym.' };
  return { success: false, error: 'Could not check preview access. Try again.' };
}

type HttpMethod = 'GET' | 'POST';

export interface PreviewReadRoute {
  method: HttpMethod;
  pattern: RegExp;
  /** Why this route is safe to call during a preview. */
  reason: string;
  /**
   * The handler file under `src/app/api`. previewLock.test.ts reads it and fails
   * if it holds a database write, an RPC, or a rate-limit call.
   */
  handler: string;
}

const UUID = '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}';
const SLUG = '[A-Za-z0-9_-]+';

export const PREVIEW_READ_ALLOWLIST: readonly PreviewReadRoute[] = [
  {
    method: 'GET',
    pattern: new RegExp(`^/api/public/programs/(class|private_lessons)/${UUID}$`, 'i'),
    reason: 'class or private lesson details read; select-only handler',
    handler: 'public/programs/[programType]/[programId]/route.ts',
  },
  {
    method: 'GET',
    pattern: /^\/api\/public\/fee-settings$/,
    reason: 'fee absorption settings read',
    handler: 'public/fee-settings/route.ts',
  },
  {
    method: 'GET',
    pattern: /^\/api\/public\/programs\/availability$/,
    reason: 'drop-in capacity and closed dates; preview calls it without athleteId',
    handler: 'public/programs/availability/route.ts',
  },
  {
    method: 'POST',
    pattern: /^\/api\/public\/programs\/proration-preview$/,
    reason: 'first-month proration and first-class date; computes only',
    handler: 'public/programs/proration-preview/route.ts',
  },
  {
    method: 'POST',
    pattern: /^\/api\/public\/programs\/sales-tax-preview$/,
    reason: 'tax rate for a section; computes only',
    handler: 'public/programs/sales-tax-preview/route.ts',
  },
  {
    method: 'GET',
    pattern: /^\/api\/policies\/pdf-preview$/,
    reason: 'renders a waiver template PDF for viewing',
    handler: 'policies/pdf-preview/route.ts',
  },
  {
    method: 'GET',
    pattern: /^\/api\/enrollment-preview\/access$/,
    reason: 'preview access check',
    handler: 'enrollment-preview/access/route.ts',
  },
  {
    method: 'GET',
    pattern: /^\/api\/enrollment-preview\/waivers$/,
    reason: 'read-only list of documents the program requires',
    handler: 'enrollment-preview/waivers/route.ts',
  },
  {
    method: 'POST',
    pattern: /^\/api\/enrollment-preview\/quote$/,
    reason: 'read-only price quote for the sample family',
    handler: 'enrollment-preview/quote/route.ts',
  },
  // Private lesson book and confirm pages (#5634 step 2). The hold routes are never listed.
  {
    method: 'GET',
    pattern: new RegExp(`^/api/public/organizations/by-slug/${SLUG}$`),
    reason: 'gym details by its page address; select-only handler',
    handler: 'public/organizations/by-slug/[gymSlug]/route.ts',
  },
  {
    method: 'GET',
    pattern: new RegExp(`^/api/public/organizations/${SLUG}$`),
    reason: 'gym details by its page address; select-only handler',
    handler: 'public/organizations/[gymSlug]/route.ts',
  },
  {
    method: 'GET',
    pattern: new RegExp(`^/api/private-lessons/${UUID}/available-slots$`, 'i'),
    reason: 'open lesson times; select-only handler, preview calls it without athleteId',
    handler: 'private-lessons/[id]/available-slots/route.ts',
  },
  {
    method: 'GET',
    pattern: /^\/api\/public\/programs\/private-lesson-location$/,
    reason: 'the location (and so the tax) of a lesson time; select-only handler',
    handler: 'public/programs/private-lesson-location/route.ts',
  },
  {
    method: 'POST',
    pattern: /^\/api\/enrollment-preview\/lesson-quote$/,
    reason: 'read-only lesson price quote for the sample family',
    handler: 'enrollment-preview/lesson-quote/route.ts',
  },
  // Tryout page and tryout register page (#5634 step 3). Register, the member-price read
  // (/pricing writes a rate-limit row) and cancel-pending are never listed.
  {
    method: 'GET',
    pattern: new RegExp(`^/api/public/tryout-events/${UUID}$`, 'i'),
    reason: 'tryout event details and lock flags; select-only handler',
    handler: 'public/tryout-events/[eventId]/route.ts',
  },
  {
    method: 'POST',
    pattern: /^\/api\/enrollment-preview\/tryout-quote$/,
    reason: 'read-only tryout price quote for the sample family',
    handler: 'enrollment-preview/tryout-quote/route.ts',
  },
  // App-wide providers keep calling these while the enroll page is open (see PREVIEW_SHELL_CALLS).
  {
    method: 'GET',
    pattern: /^\/api\/billing\/billing-only-organizations$/,
    reason: 'OrganizationContext gym list; select-only handler',
    handler: 'billing/billing-only-organizations/route.ts',
  },
  {
    method: 'GET',
    pattern: /^\/api\/app-version$/,
    reason: 'new-version check every open tab runs; returns the build id',
    handler: 'app-version/route.ts',
  },
  {
    method: 'GET',
    pattern: /^\/api\/auth\/support-session-info$/,
    reason: 'support-session banner and sign-in activity check; select-only handler',
    handler: 'auth/support-session-info/route.ts',
  },
];

/** True when `pathname` (no query string) with `method` is a preview-safe read. */
export function isPreviewReadAllowed(method: string, pathname: string): boolean {
  const upper = method.toUpperCase();
  return PREVIEW_READ_ALLOWLIST.some(
    (route) => route.method === upper && route.pattern.test(pathname),
  );
}

/** Any value marks the request as a preview; the server never trusts it as permission. */
export function isPreviewRequestHeaderValue(value: string | null | undefined): boolean {
  return value != null;
}
